import { orientationsFromConfig, orientationSummary, planesForLayoutSheet, systemDesignForOrientation, updateOrientation, layoutParcel } from "./orientations";
import { physicalLayoutDesignKey, physicalLayoutCount, layoutDesignChanged } from "../../utils/physicalLayoutDesign";
import { mountingPlaneFor } from "../../utils/mounting";
import { siteGeometryErrors } from "../../utils/siteGeometry";
import { tablesOrigin } from "../../utils/contextPlanes";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const pick = (obj, keys) => keys.map((key) => obj?.[key] ?? null);

/** Apply visually moved assemblies in the same commit as the active layout. */
export function applySceneFrames(cfg, frames = {}) {
  let next = cfg;
  orientationsFromConfig(cfg).forEach((plane) => {
    const frame = frames[plane.id];
    if (!frame || ![frame.eastM, frame.northM, frame.elevationM].every(Number.isFinite)) return;
    const saved = plane.primary ? next.pv_design : plane;
    const spacing = saved?.spacingInfo || saved?.spacing;
    if (!spacing?.site_geometry) return;
    const spacingInfo = { ...spacing, site_geometry: { ...spacing.site_geometry, frame } };
    next = plane.primary ? { ...next, pv_design: { ...next.pv_design, spacingInfo } }
      : updateOrientation(next, plane.id, { spacingInfo });
  });
  return next;
}

/** Commit shared-scene edits to their owners; the open orientation is irrelevant. */
export function applySceneLayouts(cfg, layouts = {}) {
  let next = cfg;
  orientationsFromConfig(cfg).forEach((plane) => {
    const layout = layouts[plane.id];
    if (!layout) return;
    const { ground_clearance_m: clearance, scene_views: ignored, ...spacingInfo } = layout;
    const saved = plane.primary ? next.pv_design : plane;
    const patch = { spacingInfo: { ...spacingInfo, scene_review_required: true }, sceneViews: [],
      obstacleShading: { ...saved?.obstacleShading, stale: true } };
    if (plane.primary) {
      next = { ...next, pv_design: { ...next.pv_design, ...patch },
        ...(clearance != null ? { panelOrientation: { ...next.panelOrientation, groundClearanceM: clearance } } : {}) };
    } else next = updateOrientation(next, plane.id, { ...patch,
      ...(clearance != null ? { groundClearanceM: clearance } : {}) });
  });
  return reconcileOrientationEvidence(cfg, next);
}

/** Inputs that change the receivers against which an obstacle was measured. */
function geometry(cfg, plane, planes) {
  const po = cfg?.panelOrientation || {};
  const spacing = plane.primary ? cfg?.pv_design?.spacingInfo : plane.spacingInfo;
  return [
    pick(cfg, ["read_module", "mountType", "axisOrientation", "maxAngle", "backTrack"]),
    pick(cfg?.loc_info, ["lat", "lon", "tilt", "azimuth"]),
    pick(po, ["installationType", "moduleOrientation", "roofPitch", "roofAzimuth"]),
    plane.tilt, plane.azim, plane.tiltReference,
    plane.groundClearanceM ?? po.groundClearanceM ?? null,
    layoutParcel(cfg),
    pick(spacing, ["minimum_distance_m", "pitch_m", "row_vertical_modules", "field_rows", "row_modules", "parcel_tables", "parcel_outline", "site_geometry", "string_overrides", "string_routing"]),
    [Number(spacing?.array_offset_east_m) || 0, Number(spacing?.array_offset_north_m) || 0],
    spacing?.module_offsets || {}, spacing?.module_array || null, spacing?.excluded_slots || [],
    spacing?.module_assignments || null,
    systemDesignForOrientation(cfg?.systemDesign, planes, plane.id),
  ];
}

/**
 * What every surface's shading table was calculated from, for the whole
 * project at once: one surface's change alters what the others see, so a set
 * of tables holds only for exactly this. `placements` overrides a surface's
 * imported-model placement (one moved in the open layout, not yet saved).
 */
export function sceneShadingKey(cfg, placements = {}) {
  const planes = orientationsFromConfig(cfg);
  return JSON.stringify(planes.map((plane) => {
    const saved = plane.primary ? cfg?.pv_design : plane;
    const spacing = saved?.spacingInfo || saved?.spacing || {};
    return [plane.id, geometry(cfg, plane, planes), plane.roofPitch ?? null, plane.roofAzimuth ?? null,
      spacing.module_positions || null, spacing.shading_samples_per_module ?? null, spacing.parcel_outline || null,
      placements[plane.id] !== undefined ? placements[plane.id] : (saved?.obstacleShading?.placement || null)];
  }));
}

/** A commit changes geometry and its evidence together; undo restores both. */
export function reconcileOrientationEvidence(previous, next) {
  if (!previous || !next) return next;
  const before = orientationsFromConfig(previous);
  const after = orientationsFromConfig(next);
  const sceneChanged = !same(before.map((p) => [p.id, geometry(previous, p, before)]), after.map((p) => [p.id, geometry(next, p, after)]));
  let result = next;
  after.forEach((plane) => {
    const old = before.find((o) => o.id === plane.id);
    if (!old || !sceneChanged) return;
    const oldEvidence = old.primary ? previous.pv_design : old;
    const evidence = plane.primary ? next.pv_design : plane;
    const patch = {};
    const spacing = evidence?.spacingInfo;
    if (Object.keys(spacing?.module_offsets || {}).length && !evidence?.obstacleShading) patch.obstacleShading = { stale: true };
    if (evidence?.obstacleShading && same(evidence.obstacleShading, oldEvidence?.obstacleShading)) {
      patch.obstacleShading = { ...evidence.obstacleShading, stale: true };
    }
    if (evidence?.sceneViews?.length && same(evidence.sceneViews, oldEvidence?.sceneViews)) patch.sceneViews = [];
    if (!Object.keys(patch).length) return;
    result = plane.primary
      ? { ...result, pv_design: { ...result.pv_design, ...patch } }
      : updateOrientation(result, plane.id, patch);
  });
  return result;
}

/** Actionable layout findings. Run-derived shading is prepared automatically. */
export function projectSceneReadiness(cfg, greek = false, { detailed = false, prepareShading = false } = {}) {
  const tr = (el, en) => greek ? el : en;
  const planes = orientationsFromConfig(cfg);
  const saved = planes.map((p) => p.primary ? cfg.pv_design : p);
  const summary = orientationSummary(cfg);
  const spacings = saved.map((s) => s?.spacingInfo || s?.spacing);
  const hasScene = spacings.some((s) => s?.site_geometry || s?.scene_review_required) || planes.length > 1;
  const obstacles = spacings.some((s) => s?.site_geometry?.obstacles?.length || Object.keys(s?.module_offsets || {}).length);
  const issues = [];
  saved.forEach((s, i) => {
    const spacing = spacings[i], geometry = spacing?.site_geometry;
    const label = `${tr("Επιφάνεια", "Surface")} ${i + 1}`;
    const add = (el, en) => issues.push(`${label}: ${tr(el, en)}`);
    if (!spacing) { add("λείπει αποθηκευμένη διάταξη", "saved layout missing"); return; }
    const roofLayout = ["roof_flush", "roof_frame"].includes(cfg?.panelOrientation?.installationType);
    const groundShortfall = !roofLayout && physicalLayoutCount(spacing) != null &&
      physicalLayoutCount(spacing) < (summary[i]?.modules || 0);
    if (physicalLayoutDesignKey(cfg.systemDesign) && (roofLayout || groundShortfall) &&
        layoutDesignChanged(spacing, summary[i]?.modules,
          physicalLayoutDesignKey(systemDesignForOrientation(cfg.systemDesign, planes, planes[i].id)))) {
      add(`η κάτοψη έχει ${physicalLayoutCount(spacing)} πλαίσια, ο ηλεκτρολογικός σχεδιασμός ${summary[i]?.modules || 0}. Συμφιλιώστε κάτοψη και strings`,
        `the plan has ${physicalLayoutCount(spacing)} modules, the electrical design ${summary[i]?.modules || 0}. Reconcile the plan and strings`);
    }
    if (siteGeometryErrors(geometry).length) add("μη έγκυρη γεωμετρία", "invalid geometry");
    if (planes.length > 1 && !(geometry?.frame || tablesOrigin(spacing.parcel_tables))) add("λείπει κοινή θέση", "common position missing");
    if (planes.length > 1 && geometry?.frame && spacings.some((x) => !x?.site_geometry?.frame)) add("χρησιμοποιήστε την ίδια αναφορά θέσης σε όλες τις επιφάνειες", "use the same position reference for all surfaces");
    if ((hasScene || detailed) && !spacing.module_mapping_valid) add("εκκρεμεί έλεγχος θέσεων πλαισίων και strings", "module-to-string mapping needs validation");
    // Detailed runs require validated geometry rather than an angular table.
    if (detailed) {
      if (!spacing.scene_review_required) add("αποθηκεύστε και ελέγξτε τη φυσική διάταξη για την αναλυτική εκτίμηση", "save and validate the physical layout for the detailed estimate");
      return;
    }
    // Estimation prepares missing/stale measurements from the saved scene.
    // Keep all structural checks above and require the reviewed scene format
    // that prepareSceneSimulation can reconstruct and validate.
    if (prepareShading && spacing.scene_review_required) return;
    const evidence = s?.obstacleShading;
    if (cfg?.pvLosses?.dc?.shadingMode !== "percentage" && !spacing.scene_review_required &&
        (evidence?.stale || ((obstacles || planes.length > 1) && (!evidence?.table || evidence.sceneVersion !== 2)))) {
      add("αποθηκεύστε τη διάταξη για να υπολογιστούν οι σκιάσεις", "save the layout to calculate its shading");
    } else if (cfg?.pvLosses?.dc?.shadingMode !== "percentage" && spacing.scene_review_required && spacing.module_mapping_valid &&
        (!evidence?.table || evidence.stale || evidence.sceneVersion !== 2)) {
      // Calculated before a run, not during it (sceneSimulationIssues).
      add("αποθηκεύστε τη διάταξη για να υπολογιστούν οι σκιάσεις", "save the layout to calculate its shading");
    }
  });
  return issues;
}

/** Store the measured layout and its shading in the same orientation. */
export function saveOrientationShading(cfg, id, obstacleShading, layout) {
  const plane = orientationsFromConfig(cfg).find((o) => o.id === id);
  if (!plane) return cfg;
  const { ground_clearance_m: clearance, scene_views: ignored, ...spacingInfo } = layout || {};
  const patch = { obstacleShading, ...(layout ? { spacingInfo } : {}) };
  if (!plane.primary) {
    return updateOrientation(cfg, id, {
      ...patch,
      ...(clearance != null ? { groundClearanceM: clearance } : {}),
    });
  }
  return {
    ...cfg,
    pv_design: { ...cfg.pv_design, ...patch },
    ...(clearance != null ? { panelOrientation: { ...cfg.panelOrientation, groundClearanceM: clearance } } : {}),
  };
}

/** Saved layouts for the complete design dossier; the open draft replaces its own entry. */
export function orientationReportLayouts(cfg) {
  const planes = orientationsFromConfig(cfg);
  if (planes.length < 2) return null;
  const summary = planesForLayoutSheet(cfg);
  return planes.map((plane, i) => {
    const po = { ...cfg.panelOrientation, tilt: plane.tilt, azim: plane.azim, tiltReference: plane.tiltReference };
    const saved = plane.primary ? cfg.pv_design : plane;
    const mounting = mountingPlaneFor({ ...cfg, panelOrientation: po }, saved?.spacingInfo || null);
    const originalSpacing = saved?.spacingInfo || saved?.spacing || null;
    const spacingInfo = po.installationType === "roof_flush" && originalSpacing?.site_geometry?.roof
      ? { ...originalSpacing, site_geometry: {
          ...originalSpacing.site_geometry,
          roof: { ...originalSpacing.site_geometry.roof,
            pitchDeg: mounting.tilt, azimuthDeg: mounting.azimuth },
        } }
      : originalSpacing;
    return {
      orientation: { index: i + 1, count: planes.length, name: summary[i].name, planes: summary },
      surfaceTilt: mounting.tilt, surfaceAzimuth: mounting.azimuth,
      groundClearance: plane.groundClearanceM ?? po.groundClearanceM ?? null,
      roofPitch: po.installationType === "roof_flush" ? mounting.tilt : saved?.spacingInfo?.site_geometry?.roof?.pitchDeg ?? po.roofPitch ?? 0,
      roofAzimuth: po.installationType === "roof_flush" ? mounting.azimuth : saved?.spacingInfo?.site_geometry?.roof?.azimuthDeg ?? po.roofAzimuth ?? 180,
      spacingInfo,
      sceneViews: saved?.sceneViews || [],
      obstacleShading: saved?.obstacleShading || null,
      parcel: layoutParcel(cfg),
      totalModules: summary[i].modules, capacityDcKw: summary[i].kwp,
      systemDesign: systemDesignForOrientation(cfg.systemDesign, planes, plane.id),
    };
  });
}
